import { z } from "zod";

import type { ElevationPoint } from "./elevation";

/**
 * climbCategorySchema names the classified climb categories the broadcast overlay
 * badges a climb with, in ascending severity: Cat 4 (the lightest categorized
 * climb) through Cat 1, then `hc` (hors catégorie — beyond categorization). The
 * values are the Tour-de-France roadbook vocabulary. Zod-first because the
 * category rides the resolved profile samples across the tool seam into the map
 * overlay, and the enum is the single source the leaf twin mirrors.
 */
export const climbCategorySchema = z.enum(["4", "3", "2", "1", "hc"]);

/**
 * ClimbCategory names a classified climb category, derived from
 * climbCategorySchema.
 */
export type ClimbCategory = z.infer<typeof climbCategorySchema>;

/**
 * ClimbSpan is one detected climb on the route's cumulative distance axis: the
 * `[startKm, endKm]` stretch from the climb's foot to its summit, the summit's
 * `peakElevation` (m), the climb's `avgGradePercent` (rise over run, percent),
 * and its `category`. The overlay resolves the span containing each frame's
 * distance to badge the climb and read the distance to its summit.
 */
export interface ClimbSpan {
  avgGradePercent: number;
  category: ClimbCategory;
  endKm: number;
  peakElevation: number;
  startKm: number;
}

/**
 * MIN_CATEGORY_GRADE_PERCENT is the average grade below which a stretch is never
 * a categorized climb regardless of its length — a 2 % false flat scores no
 * category even over 50 km, matching how roadbooks treat it.
 */
const MIN_CATEGORY_GRADE_PERCENT = 3;

/**
 * CATEGORY_SCORE_THRESHOLDS maps the climb score — `lengthMeters ×
 * avgGradePercent`, the widely-used Strava-style difficulty product — to a
 * category. A 4 km climb at 4 % scores 16 000 (Cat 3); an 8 km climb at 10 %
 * scores 80 000 (HC). Walked from the top so the hardest matching band wins.
 */
const CATEGORY_SCORE_THRESHOLDS: readonly { category: ClimbCategory; score: number }[] = [
  { category: "hc", score: 80_000 },
  { category: "1", score: 64_000 },
  { category: "2", score: 32_000 },
  { category: "3", score: 16_000 },
  { category: "4", score: 8_000 },
];

/**
 * climbCategoryFor classifies a climb from its length (m) and average grade (%)
 * via the score product `lengthMeters × avgGradePercent`: null under a 3 % grade
 * (never categorized) or under the Cat 4 score floor (too easy to badge), else
 * the hardest category whose threshold the score reaches. Pure and deterministic;
 * the single classification rule detectClimbs and any later climb consumer share.
 */
export const climbCategoryFor = (
  lengthMeters: number,
  avgGradePercent: number,
): ClimbCategory | null => {
  if (
    !Number.isFinite(lengthMeters) ||
    !Number.isFinite(avgGradePercent) ||
    avgGradePercent < MIN_CATEGORY_GRADE_PERCENT
  ) {
    return null;
  }

  const score = lengthMeters * avgGradePercent;
  for (const threshold of CATEGORY_SCORE_THRESHOLDS) {
    if (score >= threshold.score) {
      return threshold.category;
    }
  }

  return null;
};

/**
 * CLIMB_OPEN_WINDOW_KM is the rolling window a climb's opening grade is measured
 * over: the grade must reach the opening threshold across ~300 m of trail, so a
 * single steep step between two close points does not open a climb.
 */
const CLIMB_OPEN_WINDOW_KM = 0.3;

/**
 * CLIMB_OPEN_GRADE_PERCENT is the rolling grade at which a climb opens — the
 * conventional 3 % floor under which a road just "drags uphill".
 */
const CLIMB_OPEN_GRADE_PERCENT = 3;

/**
 * CLIMB_CLOSE_GRADE_PERCENT is the rolling grade below which a climb is
 * considered to have levelled off; sustained over CLIMB_CLOSE_WINDOW_KM it closes
 * the climb at its summit. Lower than the opening grade (hysteresis), so a brief
 * easing mid-climb does not split one ascent into two.
 */
const CLIMB_CLOSE_GRADE_PERCENT = 1;

/**
 * CLIMB_CLOSE_WINDOW_KM is how long the grade must stay below the closing
 * threshold before the climb closes — ~500 m of flat or descent ends the ascent;
 * a shorter dip is just a false flat inside it.
 */
const CLIMB_CLOSE_WINDOW_KM = 0.5;

/**
 * MIN_CLIMB_LENGTH_KM drops detected spans shorter than ~500 m: a badge on a
 * bump reads as noise, and the category score floor would reject almost all of
 * them anyway.
 */
const MIN_CLIMB_LENGTH_KM = 0.5;

/**
 * rollingGradePercent measures the grade (%) over the window ending at point
 * `index`: the elevation rise from the first point at least `windowKm` back,
 * over that ground distance. Returns null while the series has not yet covered a
 * full window (the measurement would be noise over a few metres).
 */
const rollingGradePercent = (
  points: readonly ElevationPoint[],
  index: number,
  windowKm: number,
): number | null => {
  const end = points[index];
  if (!end) {
    return null;
  }

  for (let back = index - 1; back >= 0; back -= 1) {
    const start = points[back];
    if (!start) {
      continue;
    }

    const runKm = end.distanceKm - start.distanceKm;
    if (runKm >= windowKm) {
      return ((end.elevation - start.elevation) / (runKm * 1_000)) * 100;
    }
  }

  return null;
};

/**
 * detectClimbs walks an elevation-point series and returns the categorized
 * climbs on it, ordered by their start. A climb OPENS when the rolling grade
 * over CLIMB_OPEN_WINDOW_KM reaches the opening threshold (the foot is the
 * window's start, so the badge covers the whole ramp); while open, the summit —
 * the highest elevation so far — is tracked. It CLOSES when the rolling grade
 * stays below the closing threshold for CLIMB_CLOSE_WINDOW_KM (or at the series
 * end), and the span is cut at the summit, not at the flat that followed — a
 * climb ends at its top, the roadbook convention. Spans shorter than
 * MIN_CLIMB_LENGTH_KM or scoring below Cat 4 are dropped. Pure and
 * deterministic: the same points always yield the same climbs, so the preview
 * and the recorder badge identically.
 */
export const detectClimbs = (points: readonly ElevationPoint[]): ClimbSpan[] => {
  const climbs: ClimbSpan[] = [];
  if (points.length < 2) {
    return climbs;
  }

  let open: {
    peakElevation: number;
    peakKm: number;
    startElevation: number;
    startKm: number;
  } | null = null;
  let easingSinceKm: number | null = null;

  const closeAtSummit = (): void => {
    if (!open) {
      return;
    }

    const lengthKm = open.peakKm - open.startKm;
    const riseM = open.peakElevation - open.startElevation;
    const avgGradePercent = lengthKm > 0 ? (riseM / (lengthKm * 1_000)) * 100 : 0;
    const category = climbCategoryFor(lengthKm * 1_000, avgGradePercent);
    if (lengthKm >= MIN_CLIMB_LENGTH_KM && category !== null) {
      climbs.push({
        avgGradePercent,
        category,
        endKm: open.peakKm,
        peakElevation: open.peakElevation,
        startKm: open.startKm,
      });
    }

    open = null;
    easingSinceKm = null;
  };

  for (let index = 1; index < points.length; index += 1) {
    const point = points[index];
    if (!point) {
      continue;
    }

    const grade = rollingGradePercent(points, index, CLIMB_OPEN_WINDOW_KM);
    if (grade === null) {
      continue;
    }

    if (!open) {
      if (grade >= CLIMB_OPEN_GRADE_PERCENT) {
        // The foot is the opening window's start: walk back to the point the
        // rolling window measured from, so the span covers the whole ramp.
        let footIndex = index;
        for (let back = index - 1; back >= 0; back -= 1) {
          const candidate = points[back];
          if (candidate && point.distanceKm - candidate.distanceKm >= CLIMB_OPEN_WINDOW_KM) {
            footIndex = back;
            break;
          }
        }

        const foot = points[footIndex] ?? point;
        open = {
          peakElevation: point.elevation,
          peakKm: point.distanceKm,
          startElevation: foot.elevation,
          startKm: foot.distanceKm,
        };
      }

      continue;
    }

    // Track the summit: the highest point reached while the climb is open.
    if (point.elevation > open.peakElevation) {
      open.peakElevation = point.elevation;
      open.peakKm = point.distanceKm;
      easingSinceKm = null;
    }

    // Hysteresis close: the grade must stay under the closing threshold for the
    // whole close window before the climb ends (at its summit). A grade back at
    // or above it resets the easing run — the climb goes on.
    if (grade < CLIMB_CLOSE_GRADE_PERCENT) {
      easingSinceKm ??= point.distanceKm;
      if (point.distanceKm - easingSinceKm >= CLIMB_CLOSE_WINDOW_KM) {
        closeAtSummit();
      }
    } else {
      easingSinceKm = null;
    }
  }

  closeAtSummit();

  return climbs;
};

/**
 * climbAt returns the climb span containing a cumulative distance (km), or null
 * when the point is not on a categorized climb. Containment is inclusive at both
 * ends — a frame exactly on the summit still badges the climb it just topped.
 * Spans are ordered and non-overlapping, so the first match is the match.
 */
export const climbAt = (climbs: readonly ClimbSpan[], distanceKm: number): ClimbSpan | null => {
  for (const climb of climbs) {
    if (distanceKm >= climb.startKm && distanceKm <= climb.endKm) {
      return climb;
    }

    if (climb.startKm > distanceKm) {
      break;
    }
  }

  return null;
};
